import { useState, useEffect, useMemo, useCallback, useRef, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Plus, Trash2, AlertTriangle, ArrowLeft, X, Truck } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import SearchableSelect, { type SearchableSelectOption } from '../components/SearchableSelect';
import AsyncItemSelect from '../components/AsyncItemSelect';
import ItemFormModal from '../components/ItemFormModal';
import AttachmentsSection from '../components/AttachmentsSection';
import { createBill, updateBill, getBillById, receiveBill, getVendorPayableBills, previewBillCorrection } from '../utils/billApi';
import { getBillPayments } from '../utils/billPaymentApi';
import { getBillAttachments } from '../utils/itemAttachmentApi';
import {
  listStagedAttachments,
  processAttachmentChanges,
  syncStagedAttachmentNotes,
} from '../utils/attachmentApi';
import { getSuppliers, getSupplierById, createSupplier } from '../utils/supplierApi';
import { ApiValidationError } from '../types/auth';
import {
  attachmentToEditable,
  getStagedAttachmentIds,
  hasPendingUploadFailed,
  hasPendingUploadInProgress,
  stagedAttachmentToPending,
} from '../types/attachmentsForm';
import type { EditableAttachment, PendingAttachment } from '../types/attachmentsForm';
import {
  BILL_UPLOAD_SESSION_KEY,
  getOrCreateUploadSessionId,
  resetUploadSessionId,
} from '../utils/uploadSession';
import DecimalInput from '../components/DecimalInput';
import { btnPrimary, selectInputOnFocus } from '../utils/uiTheme';
import { blurActiveInputBeforeSave } from '../utils/numberInput';
import { formatCurrency, formatPreviewQuantity } from '../utils/formatCurrency';
import BillCorrectionPreviewModal from '../components/bills/BillCorrectionPreviewModal';
import { canEditBillInUi, billEditorBlockedMessage } from '../utils/billEditability';
import {
  buildAmendLinePayload,
  buildAppendLinePayload,
  buildCostCorrectionLinePayload,
  costCorrectionRemoveBlockedReason,
  isCostCorrectionEdit,
  isNewLine,
  resolveConsumedUpdateMode,
  survivingCostCorrectionLines,
} from '../utils/billCostCorrection';
import { toast, toastApiError, toastRouteSuccess } from '../utils/toast';
import type { Bill, BillLine, BillCorrectionPreview, CreateBillData, UpdateBillData } from '../types/bill';
import type { Supplier, CreateSupplierData } from '../types/supplier';
import type { PaymentTerms } from '../types/customer';
import type { Item } from '../types/item';
import { qtyOnHandClassName } from '../utils/negativeInventory';
import { addDaysToIsoDate, todayLocalIso } from '../utils/localDate';
import { scrollIntoViewAfterPaint } from '../utils/scrollIntoViewAfterPaint';
import {
  type LinePriceSource,
  applyAmountDraftsToLines,
  commitAmountEntry,
  displayRate,
  formatLineAmountDisplay,
  lineGross,
  qtyChangePricingPatch,
  rateChangePricingPatch,
  round2,
  round4,
} from '../utils/linePricing';

const ESTIMATED_TAX_RATE = 5;
const INITIAL_EMPTY_LINE_COUNT = 4;

const supplierToOption = (supplier: Supplier): SearchableSelectOption => ({
  value: supplier.id,
  label: supplier.display_name,
  description: [supplier.email, supplier.phone].filter(Boolean).join(' · ') || undefined,
});

const dueDateFromTerms = (txnDate: string, terms: PaymentTerms) => {
  switch (terms) {
    case 'NET_30':
      return addDaysToIsoDate(txnDate, 30);
    case 'NET_60':
      return addDaysToIsoDate(txnDate, 60);
    case 'NET_90':
      return addDaysToIsoDate(txnDate, 90);
    default:
      return txnDate;
  }
};

const canManageBills = () => {
  const role = localStorage.getItem('user_role')?.toUpperCase();
  return role === 'ADMIN' || role === 'ACCOUNTANT';
};

interface LocalLine {
  bill_line_id?: number;
  item_id: number | null;
  item_name: string;
  item_sku: string;
  item_type: Item['item_type'];
  qty_on_hand: string;
  dealer_selling_price: string;
  cash_price: string;
  taxable: boolean;
  qty: number;
  original_qty: number;
  unit_price: number;
  line_amount: number | null;
  discount_percent: number;
  description: string;
  notes: string;
  price_source: LinePriceSource;
  sold_qty: number;
  min_allowed_qty: number;
  can_remove: boolean;
  markedForRemoval: boolean;
}

interface BillFormData {
  vendor_id: number;
  vendor_bill_number: string;
  txn_date: string;
  due_date: string;
  memo: string;
  lines: LocalLine[];
}

const createEmptyLine = (): LocalLine => ({
  item_id: null,
  item_name: '',
  item_sku: '',
  item_type: 'INVENTORY',
  qty_on_hand: '0',
  dealer_selling_price: '0',
  cash_price: '0',
  taxable: true,
  qty: 1,
  original_qty: 1,
  unit_price: 0,
  line_amount: null,
  discount_percent: 0,
  description: '',
  notes: '',
  price_source: 'rate',
  sold_qty: 0,
  min_allowed_qty: 0,
  can_remove: false,
  markedForRemoval: false,
});

const createInitialLines = (count = INITIAL_EMPTY_LINE_COUNT) =>
  Array.from({ length: count }, () => createEmptyLine());

const isLineEmpty = (line: LocalLine) => !line.item_id && !line.notes.trim();

const isLineActive = (line: LocalLine) => Boolean(line.item_id || line.notes.trim());

const withTrailingEmptyLine = (lines: LocalLine[]): LocalLine[] => {
  if (lines.length === 0) return [createEmptyLine()];
  if (!isLineEmpty(lines[lines.length - 1])) {
    return [...lines, createEmptyLine()];
  }
  return lines;
};

const mapBillLineToLocal = (line: BillLine): LocalLine => {
  const qty = parseFloat(line.qty);
  const soldQty = parseFloat(line.sold_qty ?? '0');
  const minAllowedQty = line.min_allowed_qty != null ? parseFloat(line.min_allowed_qty) : soldQty;

  return {
    bill_line_id: line.id,
    item_id: line.item_id,
    item_name: line.item_snapshot?.name ?? '',
    item_sku: line.item_snapshot?.sku ?? '',
    item_type: line.item?.item_type ?? 'INVENTORY',
    qty_on_hand: line.item?.qty_on_hand ?? '0',
    dealer_selling_price: line.item?.dealer_selling_price ?? '0',
    cash_price: line.item?.cash_price ?? '0',
    taxable: line.item?.taxable ?? true,
    qty,
    original_qty: qty,
    unit_price: round4(parseFloat(line.unit_price)),
    line_amount: null,
    discount_percent: parseFloat(line.discount_percent),
    description: line.description ?? '',
    notes: line.notes ?? '',
    price_source: 'rate',
    sold_qty: soldQty,
    min_allowed_qty: minAllowedQty,
    can_remove: line.can_remove ?? false,
    markedForRemoval: false,
  };
};

/** Unit rate after line discount % (informational only). */
const discountedUnitRate = (unit_price: number, discount_percent: number) =>
  round4(unit_price * (1 - discount_percent / 100));

const estimateLineTotals = (line: LocalLine) => {
  const gross = lineGross(line);
  const discountAmount = gross * (line.discount_percent / 100);
  const net = gross - discountAmount;
  const taxAmount = line.taxable ? net * (ESTIMATED_TAX_RATE / 100) : 0;
  return {
    discountAmount: round2(discountAmount),
    net: round2(net),
    taxAmount: round2(taxAmount),
    lineTotal: round2(net + taxAmount),
  };
};

const estimateBillTotals = (lines: LocalLine[]) => {
  let discountTotal = 0;
  let subtotal = 0;
  let taxTotal = 0;

  for (const line of lines.filter((line) => isLineActive(line) && !line.markedForRemoval)) {
    const { discountAmount, net, taxAmount } = estimateLineTotals(line);
    discountTotal += discountAmount;
    subtotal += net;
    taxTotal += taxAmount;
  }

  return {
    discountTotal: round2(discountTotal),
    subtotal: round2(subtotal),
    taxTotal: round2(taxTotal),
    total: round2(subtotal + taxTotal),
  };
};

const lineInputClass =
  'w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500';

const autoResize = (el: HTMLTextAreaElement) => {
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
};

export type PurchasesManageSaveReason = 'draft_created' | 'draft_updated' | 'received' | 'amended' | 'cost_corrected' | 'lines_appended';

export type PurchasesManageProps = {
  embedded?: boolean;
  billId?: number;
  onClose?: () => void;
  onBillSaved?: (bill: Bill, reason: PurchasesManageSaveReason) => void;
};

const PurchasesManage = ({
  embedded = false,
  billId: billIdProp,
  onClose,
  onBillSaved,
}: PurchasesManageProps = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryEditId = Number(searchParams.get('id')) || 0;
  const resolvedEditId =
    billIdProp != null && billIdProp > 0 ? billIdProp : queryEditId;
  const isEditMode = resolvedEditId > 0;

  const [vendorOptions, setVendorOptions] = useState<SearchableSelectOption[]>([]);
  const [vendorSearchInput, setVendorSearchInput] = useState('');
  const [vendorSearchLoading, setVendorSearchLoading] = useState(false);
  const [selectedVendorOption, setSelectedVendorOption] = useState<SearchableSelectOption | null>(
    null
  );
  const selectedVendorOptionRef = useRef<SearchableSelectOption | null>(null);
  const supplierLookupRef = useRef<Map<number, Supplier>>(new Map());
  const lineRowRefs = useRef(new Map<number, HTMLTableRowElement>());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[] | undefined>>({});
  const [showAddSupplierModal, setShowAddSupplierModal] = useState(false);
  const [showCreateItemModal, setShowCreateItemModal] = useState(false);
  const [createItemLineIndex, setCreateItemLineIndex] = useState<number | null>(null);
  const [supplierFormError, setSupplierFormError] = useState<string | null>(null);
  const [isCreatingSupplier, setIsCreatingSupplier] = useState(false);
  const [supplierFormData, setSupplierFormData] = useState<CreateSupplierData>({
    display_name: '',
    email: '',
    phone: '',
    address: '',
    payment_terms: 'NET_30',
    active: true,
  });
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<EditableAttachment[]>([]);
  const [uploadSessionId, setUploadSessionId] = useState(() =>
    getOrCreateUploadSessionId(BILL_UPLOAD_SESSION_KEY)
  );
  const [savePhase, setSavePhase] = useState<'idle' | 'saving' | 'attachments' | 'receiving' | 'preview'>('idle');
  const [editingBill, setEditingBill] = useState<Bill | null>(null);
  const [correctionPreview, setCorrectionPreview] = useState<BillCorrectionPreview | null>(null);
  const [showCorrectionPreview, setShowCorrectionPreview] = useState(false);
  const [pendingCorrectionPayload, setPendingCorrectionPayload] = useState<UpdateBillData | null>(null);
  const [pendingConsumedMode, setPendingConsumedMode] = useState<'cost_correction' | 'append'>('cost_correction');
  const [lineAmountDrafts, setLineAmountDrafts] = useState<Record<number, string>>({});
  const [loadingVendorAccount, setLoadingVendorAccount] = useState(false);
  const [vendorOpenBalance, setVendorOpenBalance] = useState(0);
  const [vendorUnappliedTotal, setVendorUnappliedTotal] = useState(0);

  const submitting = savePhase !== 'idle';
  const isDraftEdit = editingBill?.status === 'DRAFT';
  const isOpenAmend =
    editingBill?.status === 'OPEN' && editingBill?.received_into_inventory === true;
  const isCostCorrection = editingBill != null && isCostCorrectionEdit(editingBill);
  const isFullAmend = isOpenAmend && !isCostCorrection;
  const canReceive = editingBill?.received_into_inventory === false;

  const today = todayLocalIso();
  const [formData, setFormData] = useState<BillFormData>({
    vendor_id: 0,
    vendor_bill_number: '',
    txn_date: today,
    due_date: today,
    memo: '',
    lines: [],
  });
  const [billNumber, setBillNumber] = useState<string | null>(null);

  useEffect(() => {
    if (!canManageBills()) {
      if (embedded) {
        onClose?.();
      } else {
        navigate('/purchases');
      }
      return;
    }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedEditId, embedded]);

  useEffect(() => {
    if (!embedded && toastRouteSuccess(location.state)) {
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    }
  }, [embedded, location.state, location.pathname, location.search, navigate]);

  useEffect(() => {
    selectedVendorOptionRef.current = selectedVendorOption;
  }, [selectedVendorOption]);

  const loadVendorAccountSummary = useCallback(async (vendorId: number, excludeBillId = 0) => {
    setLoadingVendorAccount(true);
    try {
      const [payableBills, unappliedPaymentsList] = await Promise.all([
        getVendorPayableBills(vendorId),
        getBillPayments({ vendor_id: vendorId, unapplied: true }),
      ]);
      const openBalance = payableBills
        .filter((bill) => bill.id !== excludeBillId)
        .reduce((sum, bill) => sum + parseFloat(bill.balance), 0);
      const unapplied = unappliedPaymentsList
        .filter((payment) => (payment.status ?? 'POSTED') !== 'VOIDED')
        .reduce((sum, payment) => sum + parseFloat(payment.unapplied_amount), 0);
      setVendorOpenBalance(openBalance);
      setVendorUnappliedTotal(unapplied);
    } catch {
      setVendorOpenBalance(0);
      setVendorUnappliedTotal(0);
    } finally {
      setLoadingVendorAccount(false);
    }
  }, []);

  useEffect(() => {
    if (formData.vendor_id > 0) {
      void loadVendorAccountSummary(formData.vendor_id, resolvedEditId);
    } else {
      setVendorOpenBalance(0);
      setVendorUnappliedTotal(0);
      setLoadingVendorAccount(false);
    }
  }, [formData.vendor_id, loadVendorAccountSummary, resolvedEditId]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      setVendorSearchLoading(true);
      try {
        const result = await getSuppliers({
          search: vendorSearchInput.trim() || undefined,
          per_page: 20,
          page: 1,
        });
        for (const supplier of result.suppliers) {
          supplierLookupRef.current.set(supplier.id, supplier);
        }
        const options = result.suppliers.map(supplierToOption);
        const selected = selectedVendorOptionRef.current;
        setVendorOptions(
          selected && !options.some((opt) => String(opt.value) === String(selected.value))
            ? [selected, ...options]
            : options
        );
      } catch {
        // silently ignore — dropdown just shows empty
      } finally {
        setVendorSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [vendorSearchInput]);

  const goToEditBill = useCallback(
    (id: number) => {
      if (embedded) {
        navigate(
          { pathname: `/purchase-transactions/bill/${id}`, search: location.search },
          { replace: true, preventScrollReset: true }
        );
      } else {
        navigate(`/manage-purchases?id=${id}`, { replace: true });
      }
    },
    [embedded, location.search, navigate]
  );

  const finishReceivedOrAmended = useCallback(
    (bill: Bill, reason: 'received' | 'amended' | 'cost_corrected' | 'lines_appended') => {
      if (embedded) {
        onBillSaved?.(bill, reason);
      } else {
        navigate(`/purchase-transactions/view/bill/${bill.id}`, { replace: true });
      }
    },
    [embedded, navigate, onBillSaved]
  );

  const finishDraftCreated = useCallback(
    (bill: Bill) => {
      if (embedded) {
        onBillSaved?.(bill, 'draft_created');
      } else {
        navigate(`/manage-purchases?id=${bill.id}`, { replace: true });
      }
    },
    [embedded, navigate, onBillSaved]
  );

  const loadStagedAttachments = useCallback(async () => {
    const staged = await listStagedAttachments({
      upload_session_id: uploadSessionId,
      intended_attachable_type: 'bill',
    }).catch((err) => {
      console.error('[PurchasesManage] Failed to load staged attachments', err);
      return [];
    });
    setPendingAttachments(staged.map(stagedAttachmentToPending));
  }, [uploadSessionId]);

  const loadAttachments = useCallback(async (billId: number) => {
    const attachments = await getBillAttachments(billId).catch((err) => {
      console.error('[PurchasesManage] Failed to load attachments', err);
      return [];
    });
    setExistingAttachments(attachments.map(attachmentToEditable));
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      setBillNumber(null);
      setEditingBill(null);
      setPendingAttachments([]);
      setExistingAttachments([]);
      supplierLookupRef.current.clear();
      setVendorSearchInput('');
      setVendorOpenBalance(0);
      setVendorUnappliedTotal(0);
      setLoadingVendorAccount(false);

      if (isEditMode) {
        const bill = await getBillById(resolvedEditId);

        const blockedMessage = !canEditBillInUi(bill) ? billEditorBlockedMessage(bill) : null;
        if (blockedMessage) {
          setError(blockedMessage);
          return;
        }

        setEditingBill(bill);
        setBillNumber(bill.bill_number);

        const selectedOption: SearchableSelectOption = {
          value: bill.vendor_id,
          label: bill.vendor?.display_name ?? bill.vendor_snapshot.name,
          description:
            [bill.vendor_snapshot.email, bill.vendor_snapshot.phone]
              .filter(Boolean)
              .join(' · ') || undefined,
        };
        setSelectedVendorOption(selectedOption);
        selectedVendorOptionRef.current = selectedOption;
        setVendorOptions([selectedOption]);

        const mappedLines = (bill.lines ?? []).map((line) => mapBillLineToLocal(line));
        setFormData({
          vendor_id: bill.vendor_id,
          vendor_bill_number: bill.vendor_bill_number ?? '',
          txn_date: bill.txn_date.slice(0, 10),
          due_date: bill.due_date.slice(0, 10),
          memo: bill.memo ?? '',
          lines: withTrailingEmptyLine(mappedLines),
        });
        await loadAttachments(bill.id);
      } else {
        setSelectedVendorOption(null);
        selectedVendorOptionRef.current = null;
        setVendorOptions([]);
        setFormData((prev) => ({
          ...prev,
          lines: createInitialLines(),
        }));
      }

      await loadStagedAttachments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleVendorChange = async (vendorId: number) => {
    const id = Number(vendorId);
    if (!id) {
      setSelectedVendorOption(null);
      selectedVendorOptionRef.current = null;
      setVendorOpenBalance(0);
      setVendorUnappliedTotal(0);
      setLoadingVendorAccount(false);
      setFormData((prev) => ({
        ...prev,
        vendor_id: 0,
        due_date: dueDateFromTerms(prev.txn_date, 'NET_30'),
      }));
      return;
    }

    let supplier = supplierLookupRef.current.get(id);
    if (!supplier) {
      try {
        supplier = await getSupplierById(id);
        supplierLookupRef.current.set(id, supplier);
      } catch {
        setFormData((prev) => ({ ...prev, vendor_id: id }));
        return;
      }
    }

    const option = supplierToOption(supplier);
    setSelectedVendorOption(option);
    selectedVendorOptionRef.current = option;
    setVendorOptions((prev) =>
      prev.some((opt) => String(opt.value) === String(id)) ? prev : [option, ...prev]
    );

    const terms = supplier.payment_terms ?? 'NET_30';
    setFormData((prev) => ({
      ...prev,
      vendor_id: id,
      due_date: dueDateFromTerms(prev.txn_date, terms),
    }));
  };

  const handleTxnDateChange = (txnDate: string) => {
    setFormData((prev) => {
      const supplier = supplierLookupRef.current.get(prev.vendor_id);
      const terms = supplier?.payment_terms ?? 'NET_30';
      return {
        ...prev,
        txn_date: txnDate,
        due_date: dueDateFromTerms(txnDate, terms),
      };
    });
  };

  const resetSupplierForm = () => {
    setSupplierFormData({
      display_name: '',
      email: '',
      phone: '',
      address: '',
      payment_terms: 'NET_30',
      active: true,
    });
    setSupplierFormError(null);
  };

  const handleCreateSupplier = async (e: FormEvent) => {
    e.preventDefault();
    setIsCreatingSupplier(true);
    setSupplierFormError(null);

    try {
      if (!supplierFormData.display_name.trim()) {
        setSupplierFormError('Display name is required.');
        return;
      }

      const newSupplier = await createSupplier({
        display_name: supplierFormData.display_name.trim(),
        email: supplierFormData.email?.trim() || null,
        phone: supplierFormData.phone?.trim() || null,
        address: supplierFormData.address?.trim() || null,
        payment_terms: supplierFormData.payment_terms,
        active: supplierFormData.active,
      });
      supplierLookupRef.current.set(newSupplier.id, newSupplier);
      setShowAddSupplierModal(false);
      resetSupplierForm();
      void handleVendorChange(newSupplier.id);
      toast.success(`Vendor "${newSupplier.display_name}" created and selected.`);
    } catch (err) {
      setSupplierFormError(err instanceof Error ? err.message : 'Failed to create vendor');
    } finally {
      setIsCreatingSupplier(false);
    }
  };

  const updateLine = (index: number, patch: Partial<LocalLine>) => {
    setFormData((prev) => ({
      ...prev,
      lines: prev.lines.map((line, i) => (i === index ? { ...line, ...patch } : line)),
    }));
  };

  const handleQtyChange = (index: number, qty: number) => {
    const line = formData.lines[index];
    if (!line) return;

    const costCorrectExisting = isCostCorrection && !isNewLine(line);

    if (!qty || qty < 0.0001) {
      if (costCorrectExisting && qty < line.min_allowed_qty) {
        setError(
          `Quantity cannot be less than ${line.min_allowed_qty} (${line.sold_qty} already sold).`
        );
        return;
      }
      const removeBlockedReason = costCorrectExisting
        ? costCorrectionRemoveBlockedReason(line, formData.lines)
        : null;
      if (removeBlockedReason) {
        setError(removeBlockedReason);
        return;
      }
      setError('Line removed because quantity was set to zero.');
      removeLine(index);
      return;
    }

    if (costCorrectExisting) {
      if (qty > line.original_qty) {
        setError(`Quantity cannot exceed ${line.original_qty} on this line.`);
        return;
      }
      if (qty < line.min_allowed_qty) {
        setError(
          `Quantity cannot be less than ${line.min_allowed_qty} (${line.sold_qty} already sold).`
        );
        return;
      }
    }
    updateLine(index, qtyChangePricingPatch(line, qty));
  };

  const handleRateChange = (index: number, unit_price: number) => {
    updateLine(index, rateChangePricingPatch(unit_price));
  };

  const handleAmountFocus = (index: number, line: LocalLine) => {
    setLineAmountDrafts((prev) => ({
      ...prev,
      [index]: formatLineAmountDisplay(line, isLineActive(line)),
    }));
  };

  const handleAmountInputChange = (index: number, raw: string) => {
    setLineAmountDrafts((prev) => ({ ...prev, [index]: raw }));
  };

  const handleAmountBlur = (index: number) => {
    const raw = lineAmountDrafts[index];
    setLineAmountDrafts((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    const line = formData.lines[index];
    const committed = commitAmountEntry(
      raw ?? formatLineAmountDisplay(line, isLineActive(line)),
      line.qty
    );
    if (!committed) return;
    updateLine(index, committed);
  };

  const normalizeLines = (lines: LocalLine[]) => withTrailingEmptyLine(lines);

  const selectItemForLine = (index: number, item: Item | null) => {
    const target = formData.lines[index];
    if (isCostCorrection && target && !isNewLine(target)) return;

    setFormData((prev) => {
      const lines = [...prev.lines];
      const current = lines[index] ?? createEmptyLine();

      if (!item) {
        lines[index] = {
          ...current,
          item_id: null,
          item_name: '',
          item_sku: '',
          item_type: 'INVENTORY',
          qty_on_hand: '0',
          dealer_selling_price: '0',
          cash_price: '0',
          taxable: true,
        };
        return { ...prev, lines: normalizeLines(lines) };
      }

      lines[index] = {
        ...current,
        item_id: item.id,
        item_name: item.name,
        item_sku: item.sku,
        item_type: item.item_type,
        qty_on_hand: item.qty_on_hand,
        dealer_selling_price: item.dealer_selling_price,
        cash_price: item.cash_price,
        taxable: item.taxable,
        qty: current.qty > 0 ? current.qty : 1,
        unit_price: round4(parseFloat(item.standard_cost) || 0),
        line_amount: null,
        discount_percent: current.discount_percent,
        description: item.description?.trim() ?? '',
        notes: '',
        price_source: 'rate',
      };

      return { ...prev, lines: normalizeLines(lines) };
    });

    if (item) {
      const nextIndex = index + 1;
      scrollIntoViewAfterPaint(() => lineRowRefs.current.get(nextIndex));
    }
  };

  const openCreateItemModal = (lineIndex: number) => {
    setCreateItemLineIndex(lineIndex);
    setShowCreateItemModal(true);
  };

  const handleItemCreated = (item: Item, meta?: { imageError?: string }) => {
    if (meta?.imageError) {
      return;
    }

    if (createItemLineIndex !== null) {
      selectItemForLine(createItemLineIndex, item);
    }
    setShowCreateItemModal(false);
    setCreateItemLineIndex(null);
  };

  const setExpenseNotes = (index: number, notes: string) => {
    const target = formData.lines[index];
    if (isCostCorrection && target && !isNewLine(target)) return;

    setFormData((prev) => {
      const lines = [...prev.lines];
      const current = lines[index] ?? createEmptyLine();
      lines[index] = {
        ...current,
        notes,
        ...(notes.trim()
          ? {
              item_id: null,
              item_name: '',
              item_sku: '',
              item_type: 'INVENTORY' as const,
              qty_on_hand: '0',
              dealer_selling_price: '0',
              cash_price: '0',
              description: '',
            }
          : {}),
      };
      return { ...prev, lines: normalizeLines(lines) };
    });
  };

  const removeLine = (index: number) => {
    const target = formData.lines[index];
    if (isCostCorrection && target && !isNewLine(target)) {
      if (costCorrectionRemoveBlockedReason(target, formData.lines)) return;
      updateLine(index, { markedForRemoval: true });
      return;
    }

    setFormData((prev) => {
      if (prev.lines.length <= 1) {
        return { ...prev, lines: [createEmptyLine()] };
      }
      const lines = prev.lines.filter((_, i) => i !== index);
      return { ...prev, lines: withTrailingEmptyLine(lines) };
    });
  };

  const restoreLine = (index: number) => {
    updateLine(index, { markedForRemoval: false });
  };

  const activeLines = (lines: LocalLine[] = formData.lines) =>
    isCostCorrection ? survivingCostCorrectionLines(lines) : lines.filter(isLineActive);

  const buildLinePayload = (lines: LocalLine[] = formData.lines, appendMode = false) => {
    if (!isCostCorrection) return buildAmendLinePayload(lines);
    return appendMode ? buildAppendLinePayload(lines) : buildCostCorrectionLinePayload(lines);
  };

  const buildAttachmentFields = () => {
    const stagedIds = getStagedAttachmentIds(pendingAttachments);
    return stagedIds.length > 0 ? { staged_attachment_ids: stagedIds } : {};
  };

  const buildCreatePayload = (lines: LocalLine[] = formData.lines): CreateBillData => ({
    vendor_id: formData.vendor_id,
    vendor_bill_number: formData.vendor_bill_number.trim() || null,
    txn_date: formData.txn_date,
    due_date: formData.due_date,
    memo: formData.memo.trim() || null,
    lines: buildLinePayload(lines),
    ...buildAttachmentFields(),
  });

  const buildUpdatePayload = (
    lines: LocalLine[] = formData.lines,
    appendMode = false
  ): UpdateBillData => ({
    txn_date: formData.txn_date,
    due_date: formData.due_date,
    ...(isDraftEdit
      ? { vendor_bill_number: formData.vendor_bill_number.trim() || null }
      : {}),
    memo: formData.memo.trim() || null,
    lines: buildLinePayload(lines, appendMode),
    ...buildAttachmentFields(),
  });

  const validateForm = (lines: LocalLine[] = formData.lines) => {
    if (!formData.vendor_id) {
      setError('Please select a vendor.');
      return false;
    }
    if (formData.due_date < formData.txn_date) {
      setError('Due date must be on or after the bill date.');
      return false;
    }
    if (!formData.vendor_bill_number.trim()) {
      setError('Vendor invoice number is required.');
      return false;
    }
    if (formData.vendor_bill_number.trim().length > 50) {
      setError('Vendor invoice number is too long (max 50 characters).');
      return false;
    }
    const active = activeLines(lines);
    if (active.length === 0) {
      setError(isCostCorrection ? 'At least one line must remain on the bill.' : 'Add at least one line item.');
      return false;
    }
    for (const line of active) {
      if (!line.item_id && !line.notes.trim()) {
        setError('Each line needs a catalog item or an expense label.');
        return false;
      }
      if (line.markedForRemoval) {
        continue;
      }
      if (isCostCorrection && !isNewLine(line)) {
        if (line.qty < line.min_allowed_qty) {
          setError(
            `Quantity cannot be less than ${line.min_allowed_qty} on lines with sold inventory.`
          );
          return false;
        }
        if (line.qty > line.original_qty) {
          setError('Quantity cannot be increased during cost correction.');
          return false;
        }
      }
      if (!line.qty || line.qty < 0.0001) {
        setError('Quantity must be greater than zero on each line.');
        return false;
      }
      if (line.unit_price < 0) {
        setError('Unit cost cannot be negative.');
        return false;
      }
      if (line.discount_percent < 0 || line.discount_percent > 100) {
        setError('Discount must be between 0 and 100 on each line.');
        return false;
      }
      if (line.description.length > 1000) {
        setError('Line description is too long (max 1000 characters).');
        return false;
      }
      if (line.notes.length > 500) {
        setError('Expense label is too long (max 500 characters).');
        return false;
      }
    }
    return true;
  };

  const handleSave = async (andReceive = false) => {
    blurActiveInputBeforeSave();
    setError(null);
    setFieldErrors({});
    const saveLines = applyAmountDraftsToLines(formData.lines, lineAmountDrafts);
    if (Object.keys(lineAmountDrafts).length > 0) {
      setFormData((prev) => ({ ...prev, lines: saveLines }));
      setLineAmountDrafts({});
    }
    if (!validateForm(saveLines)) return;

    if (andReceive && isEditMode && !isDraftEdit) {
      toast.error('Only unreceived draft bills can be received from this screen.');
      return;
    }

    if (hasPendingUploadInProgress(pendingAttachments)) {
      toast.error('Please wait for file uploads to finish before saving.');
      return;
    }
    if (hasPendingUploadFailed(pendingAttachments)) {
      toast.error('Remove or retry failed file uploads before saving.');
      return;
    }

    if (isCostCorrection) {
      const mode = editingBill
        ? resolveConsumedUpdateMode(saveLines, editingBill)
        : 'cost_correction';
      if (mode === 'conflict') {
        setError(
          'Cost changes to existing lines and newly added items must be saved in separate steps.'
        );
        return;
      }
      const appendMode = mode === 'append';
      const payload = buildUpdatePayload(saveLines, appendMode);
      try {
        setSavePhase('preview');
        const preview = await previewBillCorrection(resolvedEditId, payload);
        setCorrectionPreview(preview);
        setPendingCorrectionPayload(payload);
        setPendingConsumedMode(appendMode ? 'append' : 'cost_correction');
        setShowCorrectionPreview(true);
        if (preview.blockers.length > 0) {
          setError(preview.blockers.join(' '));
        }
        if (preview.line_errors) {
          setFieldErrors(preview.line_errors);
        }
      } catch (err) {
        if (err instanceof ApiValidationError) {
          setFieldErrors(err.errors);
          setError(err.message);
        } else {
          toastApiError(err, 'Failed to preview bill correction');
        }
      } finally {
        setSavePhase('idle');
      }
      return;
    }

    await persistBill(saveLines, andReceive);
  };

  const persistBill = async (saveLines: LocalLine[], andReceive: boolean) => {
    const hasExistingAttachmentWork = existingAttachments.some(
      (row) => row.markedForDelete || row.note !== (row.attachment.note ?? '')
    );
    const hasStagedNoteWork = pendingAttachments.some(
      (row) => row.uploadStatus === 'staged' && row.note !== (row.attachment?.note ?? '')
    );

    let savedBill: Awaited<ReturnType<typeof createBill>> | null = null;

    try {
      setSavePhase('saving');

      if (hasStagedNoteWork) {
        const noteFailures = await syncStagedAttachmentNotes(pendingAttachments);
        if (noteFailures.length > 0) {
          toast.error(`Could not update attachment notes: ${noteFailures.join(' ')}`);
          return;
        }
      }

      savedBill = isEditMode
        ? await updateBill(resolvedEditId, buildUpdatePayload(saveLines))
        : await createBill(buildCreatePayload(saveLines));

      if (hasExistingAttachmentWork && canManageBills()) {
        setSavePhase('attachments');
        const { failures } = await processAttachmentChanges({
          parent: 'bill',
          parentId: savedBill.id,
          pending: [],
          existing: existingAttachments,
          showIncludeOnSend: false,
        });

        if (failures.length > 0) {
          toast.error(`Bill saved. ${failures.length} attachment issue(s): ${failures.join(' ')}`);
          setPendingAttachments([]);
          setUploadSessionId(resetUploadSessionId(BILL_UPLOAD_SESSION_KEY));
          await loadAttachments(savedBill.id);
          if (!isEditMode) {
            goToEditBill(savedBill.id);
          }
          return;
        }
      }

      setPendingAttachments([]);
      setUploadSessionId(resetUploadSessionId(BILL_UPLOAD_SESSION_KEY));
      await loadAttachments(savedBill.id);

      if (andReceive) {
        setSavePhase('receiving');
        await receiveBill(savedBill.id);
        toast.success('Bill received into inventory.');
        finishReceivedOrAmended(savedBill, 'received');
      } else if (!isEditMode) {
        toast.success('Draft bill created.');
        finishDraftCreated(savedBill);
      } else if (savedBill) {
        if (isFullAmend) {
          toast.success('Bill amended. GL, inventory, and standard cost were updated.');
          finishReceivedOrAmended(savedBill, 'amended');
        } else {
          const updated = savedBill;
          setEditingBill(updated);
          setFormData((prev) => ({
            ...prev,
            txn_date: updated.txn_date.slice(0, 10),
            due_date: updated.due_date.slice(0, 10),
            memo: updated.memo ?? '',
            lines: withTrailingEmptyLine((updated.lines ?? []).map((line) => mapBillLineToLocal(line))),
          }));
          onBillSaved?.(updated, 'draft_updated');
          toast.success('Draft bill updated.');
        }
      }
    } catch (err) {
      if (!savedBill) {
        if (err instanceof ApiValidationError) {
          setFieldErrors(err.errors);
          setError(err.message);
        } else {
          toastApiError(err, 'Failed to save bill');
        }
      } else {
        toast.error(
          `Bill saved, but could not complete the next step. ${
            err instanceof Error ? err.message : 'Please try again.'
          }`
        );
        if (!isEditMode) {
          goToEditBill(savedBill.id);
        }
      }
    } finally {
      setSavePhase('idle');
    }
  };

  const handleConfirmCorrection = async () => {
    if (!pendingCorrectionPayload) return;

    try {
      setSavePhase('saving');
      const savedBill = await updateBill(resolvedEditId, pendingCorrectionPayload);
      setShowCorrectionPreview(false);
      setCorrectionPreview(null);
      setPendingCorrectionPayload(null);
      if (pendingConsumedMode === 'append') {
        toast.success('Items added to bill. Inventory and GL were updated.');
        finishReceivedOrAmended(savedBill, 'lines_appended');
      } else {
        toast.success('Bill cost-corrected. Inventory, COGS, and GL were updated.');
        finishReceivedOrAmended(savedBill, 'cost_corrected');
      }
    } catch (err) {
      if (err instanceof ApiValidationError) {
        setFieldErrors(err.errors);
        setError(err.message);
      } else {
        toastApiError(err, 'Failed to save bill correction');
      }
    } finally {
      setSavePhase('idle');
    }
  };

  const savePrimaryLabel = isCostCorrection
    ? 'Preview & Save'
    : isFullAmend
      ? 'Save Changes'
      : 'Save Draft';

  const saveDraftLabel =
    savePhase === 'preview'
      ? 'Loading preview...'
      : savePhase === 'saving'
        ? 'Saving...'
        : savePhase === 'attachments'
          ? 'Saving attachments...'
          : savePrimaryLabel;

  const saveAndReceiveLabel =
    savePhase === 'receiving'
      ? 'Receiving...'
      : savePhase === 'saving'
        ? 'Saving...'
        : savePhase === 'attachments'
          ? 'Saving attachments...'
          : 'Save & Receive';

  const fieldError = (field: string) => fieldErrors[field]?.[0];

  const estimatedTotals = useMemo(
    () => estimateBillTotals(formData.lines),
    [formData.lines]
  );

  const showNetDiscountColumns = useMemo(
    () =>
      formData.lines.some(
        (line) =>
          isLineActive(line) &&
          !line.markedForRemoval &&
          line.discount_percent > 0
      ),
    [formData.lines]
  );

  const hasLineItems = isCostCorrection
    ? survivingCostCorrectionLines(formData.lines).length > 0
    : formData.lines.some(isLineActive);

  if (loading) {
    return (
      <div
        className={
          embedded ? 'flex flex-col h-full min-h-0 bg-gray-50' : 'flex h-screen bg-gray-50'
        }
      >
        {!embedded && <Sidebar />}
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-slate-800" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={
        embedded ? 'flex flex-col h-full min-h-0 bg-gray-50' : 'flex h-screen bg-gray-50'
      }
    >
      {!embedded && <Sidebar />}

      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
        <div className={`flex-1 overflow-auto ${embedded ? 'p-4 pb-28' : 'p-6 pb-28'}`}>
          <div className="mb-6">
            {!embedded && (
              <Link
                to="/purchases"
                className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-3"
              >
                <ArrowLeft className="h-4 w-4 mr-1.5" />
                Back to Bills
              </Link>
            )}
            <h1 className={`font-semibold text-gray-900 ${embedded ? 'text-xl' : 'text-2xl'}`}>
              {isEditMode
                ? isCostCorrection
                  ? `Cost correct bill ${editingBill?.bill_number ?? ''}`
                  : isFullAmend
                    ? `Amend Bill ${editingBill?.bill_number ?? ''}`
                    : 'Edit Draft Bill'
                : 'New Bill'}
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              {isCostCorrection
                ? 'Adjust costs, remove unsold lines, or add items missed on the original delivery. Sold invoice COGS and inventory post delta journal entries.'
                : isFullAmend
                  ? 'Changes update totals, inventory, FIFO layers, and GL immediately.'
                  : 'Create a draft, then receive when ready — inventory and GL post on receive'}
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-400 shrink-0" />
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            </div>
          )}

          {isFullAmend && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-amber-900 text-sm">
                This received bill has no payments. Saving will reverse FIFO layers and journal
                entries, apply your changes, and receive again. Fails if stock from this bill has
                already been sold.
              </p>
            </div>
          )}

          {isCostCorrection && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-amber-900 text-sm">
                Some inventory from this bill has been sold. You can adjust unit costs, reduce
                quantities down to sold amounts, or remove unsold lines. You can also add items
                left off the original delivery — new lines receipt into inventory on save. Editing
                existing lines and adding new items must be saved as separate steps. Saving posts
                delta GL entries and may adjust COGS on linked invoices.
              </p>
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 md:p-8 space-y-8">
            <section className="pb-8 border-b border-gray-100">
              <h3 className="text-base font-semibold text-gray-900 mb-5">Bill details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                <div className="sm:col-span-2 xl:col-span-2">
                  <div className={`grid grid-cols-1 gap-4 ${formData.vendor_id > 0 ? 'lg:grid-cols-2' : ''}`}>
                    <div className="min-w-0">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Vendor <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-2">
                        <SearchableSelect
                          className="flex-1 min-w-0"
                          value={formData.vendor_id}
                          onChange={(id) => void handleVendorChange(Number(id))}
                          placeholder="Select vendor"
                          searchPlaceholder="Search vendors..."
                          options={vendorOptions}
                          onSearchChange={setVendorSearchInput}
                          isLoading={vendorSearchLoading}
                          disabled={isEditMode}
                        />
                        {!isEditMode && (
                          <button
                            type="button"
                            onClick={() => {
                              resetSupplierForm();
                              setShowAddSupplierModal(true);
                            }}
                            className="flex-shrink-0 inline-flex items-center px-3 py-2.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 bg-slate-50 hover:bg-slate-100"
                            title="Create new vendor"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      {isEditMode && (
                        <p className="mt-1 text-xs text-gray-500">
                          Vendor cannot be changed after the bill is created.
                        </p>
                      )}
                      {fieldError('vendor_id') && (
                        <p className="mt-1 text-sm text-red-600">{fieldError('vendor_id')}</p>
                      )}
                    </div>

                    {formData.vendor_id > 0 && (
                      <div className="min-w-0">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Vendor account
                        </label>
                        <div className="rounded-lg border border-gray-200 bg-gray-50/50 px-3 py-2.5 space-y-1.5 text-sm">
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-gray-500">Open balance</span>
                            <span className="font-semibold text-red-600 tabular-nums whitespace-nowrap">
                              {loadingVendorAccount ? '—' : formatCurrency(vendorOpenBalance)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-gray-500">Unapplied prepayment</span>
                            <span className="font-semibold text-amber-700 tabular-nums whitespace-nowrap">
                              {loadingVendorAccount ? '—' : formatCurrency(vendorUnappliedTotal)}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {isEditMode && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Bill number</label>
                    <input
                      type="text"
                      readOnly
                      value={billNumber ?? ''}
                      className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm bg-gray-50 text-gray-500"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Vendor Invoice No <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.vendor_bill_number}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, vendor_bill_number: e.target.value }))
                    }
                    readOnly={isOpenAmend}
                    maxLength={50}
                    placeholder="Vendor's invoice or reference number"
                    className={`w-full border rounded-lg px-3.5 py-2.5 text-sm ${
                      isOpenAmend
                        ? 'border-gray-200 bg-gray-50 text-gray-500'
                        : 'border-gray-300'
                    }`}
                  />
                  {isOpenAmend && (
                    <p className="mt-1 text-xs text-gray-500">
                      Vendor bill number is frozen after receive.
                    </p>
                  )}
                  {fieldError('vendor_bill_number') && (
                    <p className="mt-1 text-sm text-red-600">{fieldError('vendor_bill_number')}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Bill date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.txn_date}
                    onChange={(e) => handleTxnDateChange(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Due date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.due_date}
                    min={formData.txn_date}
                    onChange={(e) => setFormData((prev) => ({ ...prev, due_date: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-400">Auto-set from vendor payment terms</p>
                </div>
              </div>
            </section>

            <section className="pb-8 border-b border-gray-100">
              <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
                <h3 className="text-base font-semibold text-gray-900">Line items</h3>
                <p className="text-xs text-gray-500">
                  Pick a catalog item or enter an expense label below the item field
                </p>
              </div>

              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200 text-sm table-fixed">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="w-5 px-0.5 py-2.5 text-center text-xs font-medium text-gray-500 uppercase">#</th>
                      <th className="w-[18%] px-2 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Product / service
                      </th>
                      <th className="w-[28%] px-2 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Description
                      </th>
                      <th className="w-[9%] px-2 py-2.5 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                      <th className="w-[11%] px-2 py-2.5 text-right text-xs font-medium text-gray-500 uppercase">Rate</th>
                      <th className="w-[9%] px-2 py-2.5 text-right text-xs font-medium text-gray-500 uppercase">Disc %</th>
                      <th className="w-[11%] px-2 py-2.5 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                      {showNetDiscountColumns && (
                        <th className="w-[10%] px-2 py-2.5 text-right text-xs font-medium text-gray-500 uppercase">
                          Net rate
                        </th>
                      )}
                      {showNetDiscountColumns && (
                        <th className="w-[10%] px-2 py-2.5 text-right text-xs font-medium text-gray-500 uppercase">
                          Net amount
                        </th>
                      )}
                      <th className="w-8 px-1 py-2.5" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {formData.lines.map((line, index) => {
                      const active = isLineActive(line);
                      const expenseOnly = !line.item_id && Boolean(line.notes.trim());
                      const lineDisabled = !active || line.markedForRemoval;
                      const newLine = isNewLine(line);
                      const identityDisabled =
                        line.markedForRemoval || (isCostCorrection && !newLine);
                      const removeBlockedReason = isCostCorrection
                        ? costCorrectionRemoveBlockedReason(line, formData.lines)
                        : null;
                      const rowClass = line.markedForRemoval
                        ? 'bg-red-50/60 opacity-70'
                        : isLineEmpty(line)
                          ? 'bg-white'
                          : 'bg-slate-50/40';

                      return (
                        <tr
                          key={`line-${line.bill_line_id ?? index}`}
                          ref={(el) => {
                            if (el) lineRowRefs.current.set(index, el);
                            else lineRowRefs.current.delete(index);
                          }}
                          className={`align-top ${rowClass}`}
                        >
                          <td className="w-5 px-0.5 py-2 text-center text-gray-500 tabular-nums">{index + 1}</td>
                          <td className="px-2 py-2">
                            {!expenseOnly && (
                              <div className="flex gap-1">
                                <AsyncItemSelect
                                  className="flex-1 min-w-0"
                                  value={line.item_id ?? 0}
                                  selectedLabel={line.item_name}
                                  onSelect={(item) => selectItemForLine(index, item)}
                                  onClear={() => selectItemForLine(index, null)}
                                  placeholder="Select item"
                                  searchPlaceholder="Search by name or SKU..."
                                  disabled={identityDisabled}
                                />
                                {(!isCostCorrection || newLine) && (
                                  <button
                                    type="button"
                                    onClick={() => openCreateItemModal(index)}
                                    className="flex-shrink-0 inline-flex items-center px-2 py-2.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 bg-slate-50 hover:bg-slate-100"
                                    title="Create new item"
                                  >
                                    <Plus className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            )}
                            {!line.item_id && (
                              <input
                                type="text"
                                maxLength={500}
                                value={line.notes}
                                onChange={(e) => setExpenseNotes(index, e.target.value)}
                                placeholder="Expense (freight, services…)"
                                disabled={identityDisabled}
                                className={`${lineInputClass} ${expenseOnly ? '' : 'mt-1.5'} text-sm disabled:bg-gray-50 disabled:text-gray-400`}
                              />
                            )}
                            {line.item_id && (
                              <p className="mt-1 text-xs text-gray-400 font-mono truncate">{line.item_sku}</p>
                            )}
                            {line.markedForRemoval && (
                              <p className="mt-1 text-xs font-medium text-red-600">Marked for removal</p>
                            )}
                          </td>
                          <td className="px-2 py-2">
                            <textarea
                              ref={(el) => { if (el) autoResize(el); }}
                              rows={1}
                              maxLength={1000}
                              value={line.description}
                              onChange={(e) => {
                                updateLine(index, { description: e.target.value });
                                autoResize(e.target);
                              }}
                              disabled={!line.item_id || lineDisabled}
                              placeholder={line.item_id ? 'Description' : 'For catalog items'}
                              className={`${lineInputClass} min-h-[2.25rem] resize-none overflow-hidden disabled:bg-gray-50 disabled:text-gray-400`}
                            />
                          </td>
                          <td className="px-2 py-2">
                            <DecimalInput
                              value={line.qty}
                              onCommit={(qty) => handleQtyChange(index, qty)}
                              disabled={lineDisabled}
                              className={`${lineInputClass} text-right disabled:bg-gray-50 disabled:text-gray-400`}
                            />
                            {isCostCorrection && line.sold_qty > 0 && (
                              <p className="mt-1 text-xs text-right text-amber-700">
                                Sold: {line.sold_qty}
                                {line.min_allowed_qty > 0 ? ` · min ${line.min_allowed_qty}` : ''}
                              </p>
                            )}
                            {line.item_id && (
                              <div className="mt-1 space-y-0.5 text-xs text-right">
                                {line.item_type === 'INVENTORY' && (
                                  <p className={`truncate ${qtyOnHandClassName(line.qty_on_hand)}`}>
                                    On hand: {formatPreviewQuantity(line.qty_on_hand)}
                                  </p>
                                )}
                                <p className="truncate">
                                  Dealer: {formatCurrency(line.dealer_selling_price)}
                                </p>
                                <p className="truncate">
                                  Cash: {formatCurrency(line.cash_price)}
                                </p>
                              </div>
                            )}
                          </td>
                          <td className="px-2 py-2">
                            <DecimalInput
                              value={line.unit_price}
                              onCommit={(unit_price) => handleRateChange(index, unit_price)}
                              min={0}
                              decimals={4}
                              formatDisplay={(n) => String(displayRate(n))}
                              disabled={lineDisabled}
                              className={`${lineInputClass} text-right disabled:bg-gray-50 disabled:text-gray-400`}
                            />
                          </td>
                          <td className="px-2 py-2">
                            <DecimalInput
                              value={line.discount_percent}
                              onCommit={(discount_percent) =>
                                updateLine(index, { discount_percent })
                              }
                              min={0}
                              max={100}
                              disabled={lineDisabled}
                              className={`${lineInputClass} text-right disabled:bg-gray-50 disabled:text-gray-400`}
                            />
                          </td>
                          <td className="px-2 py-2">
                            <input
                              type="number"
                              min={0}
                              step="any"
                              value={
                                lineAmountDrafts[index] ??
                                formatLineAmountDisplay(line, active && !line.markedForRemoval)
                              }
                              onFocus={(e) => {
                                handleAmountFocus(index, line);
                                selectInputOnFocus(e);
                              }}
                              onChange={(e) => handleAmountInputChange(index, e.target.value)}
                              onBlur={() => handleAmountBlur(index)}
                              disabled={lineDisabled}
                              className={`${lineInputClass} text-right disabled:bg-gray-50 disabled:text-gray-400`}
                            />
                          </td>
                          {showNetDiscountColumns && (
                            <td className="px-2 py-2 text-right tabular-nums text-gray-600">
                              {active && !line.markedForRemoval && line.discount_percent > 0
                                ? displayRate(
                                    discountedUnitRate(line.unit_price, line.discount_percent)
                                  )
                                : '—'}
                            </td>
                          )}
                          {showNetDiscountColumns && (
                            <td className="px-2 py-2 text-right tabular-nums text-gray-600">
                              {active && !line.markedForRemoval && line.discount_percent > 0
                                ? estimateLineTotals(line).net
                                : '—'}
                            </td>
                          )}
                          <td className="px-1 py-2">
                            {line.markedForRemoval ? (
                              <button
                                type="button"
                                onClick={() => restoreLine(index)}
                                className="p-1.5 text-gray-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg text-xs font-medium"
                                title="Undo removal"
                              >
                                Undo
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => removeLine(index)}
                                disabled={Boolean(removeBlockedReason)}
                                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400"
                                title={removeBlockedReason ?? 'Remove line'}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Memo</label>
                    <input
                      type="text"
                      value={formData.memo}
                      onChange={(e) => setFormData((prev) => ({ ...prev, memo: e.target.value }))}
                      placeholder="Printed on bill (e.g. PO #123)"
                      className="w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm"
                    />
                  </div>

                  <AttachmentsSection
                    title="Attachments"
                    description="Vendor invoices, receipts, and supporting documents. Files upload immediately and link when you save."
                    showIncludeOnSend={false}
                    parentIsDraft
                    stageOnPick
                    intendedAttachableType="bill"
                    uploadSessionId={uploadSessionId}
                    pending={pendingAttachments}
                    onPendingChange={setPendingAttachments}
                    existing={existingAttachments}
                    onExistingChange={setExistingAttachments}
                    disabled={submitting}
                  />
                </div>

                <div className="bg-slate-50 rounded-xl border border-slate-200 p-5">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-4">
                    Estimated breakdown
                  </p>
                  {hasLineItems ? (
                    <div className="space-y-2.5 text-sm">
                      {estimatedTotals.discountTotal > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Line discounts</span>
                          <span className="font-medium text-red-600">
                            −{formatCurrency(estimatedTotals.discountTotal)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-600">Subtotal</span>
                        <span className="font-medium text-gray-900">
                          {formatCurrency(estimatedTotals.subtotal)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Tax (est. {ESTIMATED_TAX_RATE}%)</span>
                        <span className="font-medium text-gray-900">
                          {formatCurrency(estimatedTotals.taxTotal)}
                        </span>
                      </div>
                      <div className="flex justify-between pt-3 mt-1 border-t border-slate-200">
                        <span className="text-base font-semibold text-gray-900">Total</span>
                        <span className="text-base font-bold text-slate-800">
                          {formatCurrency(estimatedTotals.total)}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">Add line items to see totals</p>
                  )}
                  <p className="text-xs text-gray-500 mt-4">
                    Tax estimate uses {ESTIMATED_TAX_RATE}% on taxable lines. Server applies company tax codes on save.
                  </p>
                </div>
              </div>
            </section>
          </div>
        </div>

        <div className="flex-shrink-0 border-t border-gray-200 bg-white px-6 py-4 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="text-sm text-gray-600">
              {hasLineItems ? (
                <>
                  <span className="text-gray-500 mr-3">Subtotal {formatCurrency(estimatedTotals.subtotal)}</span>
                  <span className="text-gray-500 mr-3">Tax {formatCurrency(estimatedTotals.taxTotal)}</span>
                  <span className="text-lg font-bold text-slate-800">{formatCurrency(estimatedTotals.total)}</span>
                </>
              ) : (
                <span className="text-gray-400">No line items</span>
              )}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => (embedded ? onClose?.() : navigate('/purchases'))}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleSave(false)}
                disabled={submitting || !hasLineItems}
                className={`${btnPrimary} px-6 py-2.5 text-sm font-semibold disabled:opacity-50`}
              >
                {saveDraftLabel}
              </button>
              {(!isEditMode || (isDraftEdit && canReceive)) && (
                <button
                  type="button"
                  onClick={() => handleSave(true)}
                  disabled={submitting || !hasLineItems}
                  className="px-6 py-2.5 text-sm font-semibold rounded-lg text-white bg-green-700 hover:bg-green-800 disabled:opacity-50"
                >
                  {saveAndReceiveLabel}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {showAddSupplierModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <div className="flex items-center gap-3">
                <Truck className="h-6 w-6 text-slate-700" />
                <h3 className="text-lg font-semibold">Add Vendor</h3>
              </div>
              <button type="button" onClick={() => setShowAddSupplierModal(false)}>
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>
            <form onSubmit={handleCreateSupplier}>
              <div className="p-6 space-y-4">
                {supplierFormError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                    {supplierFormError}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium mb-1">Display Name *</label>
                  <input
                    type="text"
                    required
                    value={supplierFormData.display_name}
                    onChange={(e) =>
                      setSupplierFormData((prev) => ({ ...prev, display_name: e.target.value }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Email</label>
                    <input
                      type="email"
                      value={supplierFormData.email ?? ''}
                      onChange={(e) => setSupplierFormData((prev) => ({ ...prev, email: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Phone</label>
                    <input
                      type="tel"
                      value={supplierFormData.phone ?? ''}
                      onChange={(e) => setSupplierFormData((prev) => ({ ...prev, phone: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm"
                    />
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowAddSupplierModal(false)}
                  className="px-4 py-2 border rounded-lg text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreatingSupplier}
                  className="px-4 py-2 rounded-lg text-sm text-white bg-slate-800 disabled:opacity-60"
                >
                  {isCreatingSupplier ? 'Creating...' : 'Create Vendor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCreateItemModal && (
        <ItemFormModal
          subtitle="Adds the item to your catalog and selects it on this line."
          onClose={() => {
            setShowCreateItemModal(false);
            setCreateItemLineIndex(null);
          }}
          onSaved={handleItemCreated}
        />
      )}

      <BillCorrectionPreviewModal
        isOpen={showCorrectionPreview}
        preview={correctionPreview}
        isConfirming={savePhase === 'saving'}
        onConfirm={handleConfirmCorrection}
        onCancel={() => {
          setShowCorrectionPreview(false);
          setCorrectionPreview(null);
          setPendingCorrectionPayload(null);
        }}
      />
    </div>
  );
};

export default PurchasesManage;
